/**
 * ProductDetail Page
 *
 * Read-only view of a single product. Fetches by id from /api/products/:id.
 * Admin users see Edit and Deactivate/Reactivate controls.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import api from '../services/api';
import hierarchyService from '../services/hierarchyService';
import { ROUTES } from '../config/constants';
import ProductTypeIcon from '../components/ProductTypeIcon';
import './ProductDetail.css';

const PRODUCT_TYPE_LABELS = {
  TECHNICAL: 'Technical Product',
  COMMERCIAL: 'Commercial Product',
  PACKAGE: 'Package',
};

const FIELD_LABELS = {
  product_name: 'Product Name',
  product_code: 'Product Code',
  description: 'Description',
  owner_id: 'Owner',
  categorization: 'Categorization',
  status: 'Status',
  product_type: 'Product Type',
  release_type: 'Release Type',
  business_unit: 'Business Unit',
  performance_matrix_group: 'Performance Matrix Group',
  product_family: 'Product Family',
  availability_date: 'Availability Date',
  installation: 'Installation',
  end_of_life_date: 'End of Life Date',
  discontinued_date: 'Discontinued Date',
  core_product_type: 'Core Product Type',
  has_customer_update_adoption_criteria: 'Has Customer Update Adoption Criteria',
  based_on: 'Based On',
  predecessor_product: 'Predecessor Product',
  fulfilment_qty_calc_method: 'Fulfilment Qty Calculation Method',
  ppp_apply: 'PPP Apply',
  provisioning_required: 'Provisioning Required',
  provisioning_team: 'Provisioning Team',
  provisioning_automated: 'Provisioning Automated',
  provisioned_in_npe_prod: 'Provisioned in NPE/Prod',
  has_provisioning_action_on_renewal: 'Has Provisioning Action on Renewal',
};

const ProductDetail = () => {
  const { id } = useParams();
  const history = useHistory();

  const [product, setProduct] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [linkedPackages, setLinkedPackages] = useState([]);

  const [activeTab, setActiveTab] = useState('details');
  const [productHistory, setProductHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.getCurrentUser();
        setCurrentUser(response.data.data.user || response.data.data);
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    fetchProduct();
    setActiveTab('details');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getProductById(id);
      const prod = response.data.data.product || response.data.data;
      setProduct(prod);

      if (prod.product_type === 'COMMERCIAL') {
        const res = await hierarchyService.getTechnicalProductsForCommercial(prod.id);
        const all = res.data.data.technicalProducts || [];
        setLinkedProducts(all.filter(p => p.linked));
        const pkgRes = await hierarchyService.getPackagesForCommercial(prod.id);
        setLinkedPackages(pkgRes.data.data.packages || []);
      } else if (prod.product_type === 'PACKAGE') {
        const res = await hierarchyService.getCommercialProductsForPackage(prod.id);
        setLinkedProducts(res.data.data.linked || []);
      } else if (prod.product_type === 'TECHNICAL') {
        const res = await hierarchyService.getCommercialProductsForTechnical(prod.id);
        setLinkedProducts(res.data.data.commercialProducts || []);
      }
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.message || 'Failed to load product.');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await api.getProductHistory(id);
      setProductHistory(response.data.data.history);
    } catch (err) {
      setHistoryError('Failed to load change history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  const handleEdit = () => {
    history.push(`/products/${id}/edit`);
  };

  const handleBack = () => {
    history.push(ROUTES.DASHBOARD);
  };

  const handleToggleStatus = async () => {
    if (!product) return;
    const newStatus = product.status === 'active' ? 'inactive' : 'active';
    const confirmMsg = newStatus === 'inactive'
      ? `Are you sure you want to deactivate "${product.product_name}"?`
      : `Are you sure you want to reactivate "${product.product_name}"?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setStatusUpdating(true);
      await api.setProductStatus(id, newStatus);
      setProduct(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error('Error updating product status:', err);
      alert('Failed to update product status: ' + (err.message || 'Unknown error'));
    } finally {
      setStatusUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  if (loading) {
    return (
      <div className="product-detail__loading">
        <div className="product-detail__spinner" />
        <p>Loading product...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail__error">
        <p>{error}</p>
        <button className="product-detail__btn product-detail__btn--secondary" onClick={handleBack}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!product) return null;

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="product-detail">
      <div className="product-detail__header">
        <div className="product-detail__header-left">
          <button className="product-detail__back-btn" onClick={handleBack}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to Dashboard
          </button>
          <div className="product-detail__title-row">
            <ProductTypeIcon productType={product.product_type} size={24} />
            <h1 className="product-detail__title">{product.product_name}</h1>
            <span className="product-detail__status-badge" data-status={product.status}>
              {product.status || 'unknown'}
            </span>
          </div>
        </div>
        {isAdmin && (
          <div className="product-detail__admin-actions">
            <button className="product-detail__btn product-detail__btn--secondary" onClick={handleEdit}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit
            </button>
            <button
              className={`product-detail__btn ${product.status === 'active' ? 'product-detail__btn--danger' : 'product-detail__btn--success'}`}
              onClick={handleToggleStatus}
              disabled={statusUpdating}
            >
              {statusUpdating ? 'Updating...' : product.status === 'active' ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="product-detail__tabs">
        <button
          className={`product-detail__tab${activeTab === 'details' ? ' product-detail__tab--active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`product-detail__tab${activeTab === 'history' ? ' product-detail__tab--active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      <div className="product-detail__body">

        {/* Details tab */}
        {activeTab === 'details' && (
          <>
            <div className="product-detail__section">
              <h2 className="product-detail__section-title">Product Information</h2>
              <div className="product-detail__grid">
                <div className="product-detail__field">
                  <span className="product-detail__field-label">Product Name</span>
                  <span className="product-detail__field-value">{product.product_name || '—'}</span>
                </div>

                <div className="product-detail__field">
                  <span className="product-detail__field-label">Product Type</span>
                  <span className="product-detail__field-value">
                    <ProductTypeIcon productType={product.product_type} size={14} />
                    {PRODUCT_TYPE_LABELS[product.product_type] || product.product_type || '—'}
                  </span>
                </div>

                <div className="product-detail__field">
                  <span className="product-detail__field-label">Release Type</span>
                  <span className="product-detail__field-value">{product.release_type || '—'}</span>
                </div>

                <div className="product-detail__field">
                  <span className="product-detail__field-label">Product Code</span>
                  <span className="product-detail__field-value">{product.product_code || '—'}</span>
                </div>

                <div className="product-detail__field product-detail__field--full">
                  <span className="product-detail__field-label">Description</span>
                  <span className="product-detail__field-value product-detail__field-value--multiline">
                    {product.description || '—'}
                  </span>
                </div>

                <div className="product-detail__field">
                  <span className="product-detail__field-label">Categorization</span>
                  <span className="product-detail__field-value">{product.categorization || '—'}</span>
                </div>

                <div className="product-detail__field">
                  <span className="product-detail__field-label">Owner</span>
                  <span className="product-detail__field-value">
                    {product.owner
                      ? `${product.owner.first_name || ''} ${product.owner.last_name || ''}`.trim() || '—'
                      : '—'
                    }
                  </span>
                </div>

                <div className="product-detail__field">
                  <span className="product-detail__field-label">Status</span>
                  <span className="product-detail__status-badge" data-status={product.status}>
                    {product.status || '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Detail section — Commercial */}
            {product.product_type === 'COMMERCIAL' && (
              <div className="product-detail__section">
                <h2 className="product-detail__section-title">Detail</h2>
                <div className="product-detail__grid">
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Business Unit</span>
                    <span className="product-detail__field-value">{product.business_unit || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Performance Matrix Group</span>
                    <span className="product-detail__field-value">{product.performance_matrix_group || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Product Family</span>
                    <span className="product-detail__field-value">{product.product_family || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Based On</span>
                    <span className="product-detail__field-value">{product.based_on || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Availability Date</span>
                    <span className="product-detail__field-value">{product.availability_date || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Installation</span>
                    <span className="product-detail__field-value">{product.installation || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">End Of Life Date</span>
                    <span className="product-detail__field-value">{product.end_of_life_date || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Discontinued Date</span>
                    <span className="product-detail__field-value">{product.discontinued_date || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Core Product Type</span>
                    <span className="product-detail__field-value">{product.core_product_type || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Predecessor Product</span>
                    <span className="product-detail__field-value">{product.predecessor_product || '—'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Detail section — Technical */}
            {product.product_type === 'TECHNICAL' && (
              <div className="product-detail__section">
                <h2 className="product-detail__section-title">Detail</h2>
                <div className="product-detail__grid">
                  <div className="product-detail__field product-detail__field--full">
                    <span className="product-detail__field-label">Fulfilment Quantity Calculation Method</span>
                    <span className="product-detail__field-value product-detail__field-value--multiline">
                      {product.fulfilment_qty_calc_method || '—'}
                    </span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Polaris ID</span>
                    <span className="product-detail__field-value">{product.polaris_id || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Predecessor Product</span>
                    <span className="product-detail__field-value">{product.predecessor_product || '—'}</span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">PPP Apply?</span>
                    <span className="product-detail__field-value">
                      {product.ppp_apply === true ? 'Yes' : product.ppp_apply === false ? 'No' : '—'}
                    </span>
                  </div>
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Has Customer Update Adoption Criteria?</span>
                    <span className="product-detail__field-value">{product.has_customer_update_adoption_criteria ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Provisioning section — Technical only */}
            {product.product_type === 'TECHNICAL' && (
              <div className="product-detail__section">
                <h2 className="product-detail__section-title">Provisioning</h2>
                <div className="product-detail__grid">
                  <div className="product-detail__field">
                    <span className="product-detail__field-label">Provisioning Required?</span>
                    <span className="product-detail__field-value">
                      {product.provisioning_required === true ? 'Yes'
                        : product.provisioning_required === false ? 'No'
                        : '—'}
                    </span>
                  </div>
                  {product.provisioning_required === true && (
                    <>
                      <div className="product-detail__field">
                        <span className="product-detail__field-label">Provisioning Team</span>
                        <span className="product-detail__field-value">{product.provisioning_team || '—'}</span>
                      </div>
                      <div className="product-detail__field">
                        <span className="product-detail__field-label">Provisioning Automated?</span>
                        <span className="product-detail__field-value">
                          {product.provisioning_automated === true ? 'Yes'
                            : product.provisioning_automated === false ? 'No'
                            : '—'}
                        </span>
                      </div>
                      <div className="product-detail__field">
                        <span className="product-detail__field-label">Provisioned in NPE/Prod</span>
                        <span className="product-detail__field-value">{product.provisioned_in_npe_prod || '—'}</span>
                      </div>
                      <div className="product-detail__field">
                        <span className="product-detail__field-label">Has Provisioning Action on Renewal</span>
                        <span className="product-detail__field-value">
                          {product.has_provisioning_action_on_renewal === true ? 'Yes'
                            : product.has_provisioning_action_on_renewal === false ? 'No'
                            : '—'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {product.product_type === 'COMMERCIAL' && (
              <div className="product-detail__section">
                <h2 className="product-detail__section-title">Linked Packages</h2>
                {linkedPackages.length === 0 ? (
                  <p className="product-detail__linked-empty">Not linked to any packages.</p>
                ) : (
                  <ul className="product-detail__linked-list">
                    {linkedPackages.map(p => (
                      <li key={p.id} className="product-detail__linked-item">
                        <ProductTypeIcon productType={p.product_type} size={14} />
                        <span
                          className="product-detail__linked-name"
                          onClick={() => history.push(`/products/${p.id}`)}
                        >
                          {p.product_name}
                        </span>
                        <span className={`product-detail__linked-status product-detail__linked-status--${p.status}`}>
                          {p.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {(product.product_type === 'COMMERCIAL' || product.product_type === 'PACKAGE' || product.product_type === 'TECHNICAL') && (
              <div className="product-detail__section">
                <h2 className="product-detail__section-title">
                  {product.product_type === 'COMMERCIAL' ? 'Linked Technical Products'
                    : product.product_type === 'PACKAGE' ? 'Linked Commercial Products'
                    : 'Linked Commercial Products'}
                </h2>
                {linkedProducts.length === 0 ? (
                  <p className="product-detail__linked-empty">No linked products.</p>
                ) : (
                  <ul className="product-detail__linked-list">
                    {linkedProducts.map(p => (
                      <li key={p.id} className="product-detail__linked-item">
                        <ProductTypeIcon productType={p.product_type} size={14} />
                        <span
                          className="product-detail__linked-name"
                          onClick={() => history.push(`/products/${p.id}`)}
                        >
                          {p.product_name}
                        </span>
                        <span className={`product-detail__linked-status product-detail__linked-status--${p.status}`}>
                          {p.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}

        {/* History tab */}
        {activeTab === 'history' && (
          <div className="product-detail__section">
            <h2 className="product-detail__section-title">Change History</h2>
            {historyLoading && (
              <div className="product-detail__history-loading">
                <div className="product-detail__spinner" />
                <p>Loading history...</p>
              </div>
            )}
            {historyError && (
              <p className="product-detail__history-error">{historyError}</p>
            )}
            {!historyLoading && !historyError && productHistory.length === 0 && (
              <p className="product-detail__linked-empty">No changes have been recorded yet.</p>
            )}
            {!historyLoading && productHistory.length > 0 && (
              <ul className="product-detail__history-list">
                {productHistory.map((entry) => (
                  <li key={entry.id} className="product-detail__history-entry">
                    <div className="product-detail__history-meta">
                      <span className="product-detail__history-field">
                        {FIELD_LABELS[entry.field_name] || entry.field_name}
                      </span>
                      <span className="product-detail__history-date">{formatDate(entry.changed_at)}</span>
                    </div>
                    <div className="product-detail__history-change">
                      <span className="product-detail__history-old">
                        {entry.old_value != null && entry.old_value !== '' ? entry.old_value : <em>—</em>}
                      </span>
                      <span className="product-detail__history-arrow">→</span>
                      <span className="product-detail__history-new">
                        {entry.new_value != null && entry.new_value !== '' ? entry.new_value : <em>—</em>}
                      </span>
                    </div>
                    {entry.changedBy && (
                      <div className="product-detail__history-by">
                        by {entry.changedBy.first_name
                          ? `${entry.changedBy.first_name} ${entry.changedBy.last_name}`.trim()
                          : entry.changedBy.email}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
